import React, { FC, useCallback, useState } from "react";
import { connect } from "react-redux";

import { faFolder, faFolderXmark } from "@fortawesome/pro-regular-svg-icons";
import { faFolderOpen } from "@fortawesome/pro-regular-svg-icons";
import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import { useIntl } from "react-intl";
import { Link, useHistory, useParams } from "react-router-dom";

import Folder from "@mapmycustomers/shared/types/entity/Folder";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { InlineTextField } from "@mapmycustomers/ui";

import Path from "@app/enum/Path";
import { getCurrentUserId } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";

import { getFolders, getSharedFolders, getTrashFolder, getUserFolders } from "../../store";
import { createFolder, updateFolder } from "../../store/actions";
import { ALL_RESOURCES_FOLDER } from "../../utils/const";

import ActionButton from "./ActionButton";
import messages from "./messages";
import styles from "./Sidebar.module.scss";
import StorageInfo from "./StorageInfo";

interface SidebarProps {
  myFolders: Folder[];
  onCreateFolder: typeof createFolder.request;
  onUpdateFolder: typeof updateFolder.request;
  sharedFolders: Folder[];
  trashFolder?: Folder;
}

const Sidebar: FC<SidebarProps> = ({
  myFolders,
  onCreateFolder,
  onUpdateFolder,
  sharedFolders,
  trashFolder,
}) => {
  const history = useHistory();
  const intl = useIntl();
  const analytics = useAnalytics();

  const { folderId } = useParams<{ folderId?: string }>();

  const [createFolder, showCreateFolder, hideCreateFolder] = useBoolean();
  const [folderToRename, setFolderToRename] = useState<Folder>();

  const activeFolderId = folderId && parseInt(folderId);

  const handleCreateFolder = useCallback(
    (name: string) => {
      onCreateFolder({
        callback: (id: Folder["id"]) => {
          history.push(`${Path.RESOURCES}/folders/${id}`);
          analytics.completed(["Add Folder"]);
        },
        name,
      });
      hideCreateFolder();
    },
    [analytics, hideCreateFolder, history, onCreateFolder]
  );

  const handleRenameFolder = useCallback(
    (name: string) => {
      if (folderToRename) {
        onUpdateFolder({
          callback: () => {
            setFolderToRename(undefined);
          },
          folder: {
            id: folderToRename.id,
            name: name,
          },
        });
      }
    },
    [folderToRename, onUpdateFolder]
  );

  const handleRenameCancel = useCallback(() => {
    setFolderToRename(undefined);
  }, []);

  const handleBlur = useCallback(
    (e) => {
      // To prevent blur action when cancel button is clicked as we save text onblur
      if (e.relatedTarget && e.relatedTarget.classList.contains("mmc-js-save-on-blur")) {
        e.preventDefault();
        return;
      }
      if (folderToRename) {
        handleRenameCancel();
      } else {
        hideCreateFolder();
      }
    },
    [folderToRename, handleRenameCancel, hideCreateFolder]
  );

  return (
    <div className={styles.container}>
      <div className={styles.myFolders}>
        <div className={styles.foldersTitle}>
          {intl.formatMessage(messages.myFolders)}
          <FontAwesomeIcon
            className={styles.addBtn}
            icon={faPlus}
            onClick={showCreateFolder}
            size="lg"
          />
        </div>
        <Link
          className={cn(styles.folder, {
            [styles.activeFolder]: !activeFolderId,
          })}
          key={ALL_RESOURCES_FOLDER}
          to={`${Path.RESOURCES}`}
        >
          <div className={styles.folderName}>
            <FontAwesomeIcon
              className={styles.folderIcon}
              icon={!activeFolderId ? faFolderOpen : faFolder}
              size="lg"
            />
            <span>{intl.formatMessage(messages.allResources)}</span>
          </div>
        </Link>
        {myFolders.map((folder: Folder) =>
          folderToRename?.id === folder.id ? (
            <div className={styles.renameFolder} key={folder.id}>
              <FontAwesomeIcon
                className={cn(styles.folderIcon, styles.renameFolderIcon)}
                icon={faFolder}
                size="lg"
              />
              <InlineTextField
                className={styles.textField}
                defaultEditing
                onBlur={handleBlur}
                onCancel={handleRenameCancel}
                onChange={handleRenameFolder}
                size="middle"
                value={folder.name}
              />
            </div>
          ) : (
            <Link
              className={cn(styles.folder, { [styles.activeFolder]: activeFolderId === folder.id })}
              key={folder.id}
              to={`${Path.RESOURCES}/folders/${folder.id}`}
            >
              <div className={styles.folderName}>
                <FontAwesomeIcon
                  className={styles.folderIcon}
                  icon={activeFolderId === folder.id ? faFolderOpen : faFolder}
                  size="lg"
                />
                <span className={styles.name}>{folder.name}</span>
              </div>
              <ActionButton folder={folder} onSetFolder={setFolderToRename} />
            </Link>
          )
        )}
        {createFolder && (
          <div className={styles.newFolder}>
            <FontAwesomeIcon className={styles.folderIcon} icon={faFolder} size="lg" />
            <InlineTextField
              className={styles.textField}
              defaultEditing
              onBlur={handleBlur}
              onCancel={hideCreateFolder}
              onChange={handleCreateFolder}
              size="middle"
            />
          </div>
        )}
        {trashFolder && (
          <Link
            className={cn(styles.trashFolder, {
              [styles.activeFolder]: activeFolderId === trashFolder.id,
            })}
            key={trashFolder.id}
            to={`${Path.RESOURCES}/folders/${trashFolder.id}`}
          >
            <div className={styles.folderName}>
              <FontAwesomeIcon className={styles.folderIcon} icon={faFolderXmark} size="lg" />
              <span>{intl.formatMessage(messages.trash)}</span>
            </div>
            <ActionButton folder={trashFolder} isTrash onSetFolder={setFolderToRename} />
          </Link>
        )}
      </div>
      {sharedFolders.length > 0 && (
        <div className={styles.sharedFolders}>
          <div className={styles.foldersTitle}>{intl.formatMessage(messages.sharedFolders)}</div>
          {sharedFolders.map(({ id, name }) => (
            <Link
              className={cn(styles.folder, { [styles.activeFolder]: activeFolderId === id })}
              key={id}
              to={`${Path.RESOURCES}/folders/${id}`}
            >
              <div className={styles.folderName}>
                <FontAwesomeIcon
                  className={styles.folderIcon}
                  icon={activeFolderId === id ? faFolderOpen : faFolder}
                  size="lg"
                />
                <span className={styles.name}>{name}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
      <StorageInfo />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUserId: getCurrentUserId(state)!,
  folders: getFolders(state),
  myFolders: getUserFolders(state),
  sharedFolders: getSharedFolders(state),
  trashFolder: getTrashFolder(state),
});

const mapDispatchToProps = {
  onCreateFolder: createFolder.request,
  onUpdateFolder: updateFolder.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(Sidebar);
